import React, { useEffect, useRef, useState } from "react";
import clsx from "clsx";
import { Button, Card, DateIntervalField, Form, MenuItem, SelectField, TextField } from "@curves/ui-base";
import { Grid } from "@mui/material";
import useOrderApi from "@apis/useOrderApi";
import { getCurrentEndDay, getCurrentStartDate, parseDate } from "@util/moment";
import { useAccountBookContext } from "./components/AccountBookContext";
import { initBookmark } from "./staticData";
import { isEmpty, objectToQueryString, paramsToObject } from "@util/utils";
import { ACCOUNT_BOOK_ORDERS_OVERVIEW_URL } from "@icoach/router/routerPath";
import { useHistory } from "react-router-dom";

/**
 * 一般訂單搜尋欄組件
 * 專門處理現場訂單和批次訂單的搜尋邏輯
 * 搜尋欄位：訂單/購買人、建立日期、購買人身份、狀態、訂單類型
 * API：postOrderOverviewAdvanceApi
 */

const AccountBookOrderOverviewSearchBar = props => {
	const { className } = props;
	const history = useHistory();
	const { postOrderOverviewAdvanceApi, getOrdersOptionsApi } = useOrderApi();
	const { activeTab, setTableData, bookmark, setBookmarkNum, setRefresh } = useAccountBookContext();
	const [paramsData, setParamsData] = useState({});
	const { name = "", startDate = "", endDate = "", orderType = "99", status = "99", receiverType = "99" } = paramsData || {};
	const [options, setOptions] = useState({});
	const { integrateStatusOptions = [], receiverTypeOptions = [], orderTypeOptions = [] } = options;
	const nameRef = useRef(null);
	const dateRangeRef = useRef(null);
	const receiverTypeRef = useRef(null);
	const statusRef = useRef(null);
	const orderTypeRef = useRef(null);
	const preloadRef = useRef(false);
	const refreshQueryRef = useRef(false);

	/**
	 * 處理搜尋按鈕點擊
	 */
	const handleSearchOnClick = () => {
		const params = getParams(initBookmark(), activeTab);
		preloadRef.current = false;
		getOverviewApi(params);
		setBookmarkNum(1);
	};
    
	/**
	 * 更新 URL 查詢參數
	 */
	const updateUrlWithSearchQuery = params => {
		let url = ACCOUNT_BOOK_ORDERS_OVERVIEW_URL;
		if (!isEmpty(params)) url += objectToQueryString(params);
		history.replace(url);
	};

	const getQueryStringParams = () => {
		const search = history.location.search;
		const { orderSourceType = activeTab, name = "", startDate = "", endDate = "", receiverType = "99", status = "99", orderType = "99" } = paramsToObject(search);
		return { orderSourceType, name, startDate, endDate, receiverType, status, orderType };
	};
	/**
	 * 獲取搜尋參數
	 * 一般訂單參數：orderSourceType, name, startDate, endDate, receiverType, status, orderType
	 */
	const getParams = (pageInfo = {}, currentOrderSourceType) => {
		const result = {};

		if (pageInfo) {
			Object.assign(result, pageInfo);
		}

		// 一般訂單參數
		if (currentOrderSourceType) {
			Object.assign(result, { orderSourceType: currentOrderSourceType });
		}

		if (nameRef.current && nameRef.current.getResult) {
			Object.assign(result, { name: nameRef.current.getResult() });
		}

		if (dateRangeRef.current && dateRangeRef.current.getResult) {
			const { startDate, endDate } = dateRangeRef.current.getResult();
			Object.assign(result, { startDate: getCurrentStartDate(startDate), endDate: getCurrentEndDay(endDate) });
		}

		if (receiverTypeRef.current && receiverTypeRef.current.getResult) {
			Object.assign(result, { receiverType: receiverTypeRef.current.getResult() || 0 });
		}

		if (statusRef.current && statusRef.current.getResult) {
			Object.assign(result, { status: statusRef.current.getResult() || 0 });
		}

		if (orderTypeRef.current && orderTypeRef.current.getResult) {
			Object.assign(result, { orderType: orderTypeRef.current.getResult() || 0 });
		}

		return result;
	};
	/**
	 * 調用一般訂單 API
	 */
	const getOverviewApi = async params => {
		const res = await postOrderOverviewAdvanceApi(params);
		if (res) setTableData(res);
		const par = { ...params, activeTab };
		updateUrlWithSearchQuery(par);
		preloadRef.current = true;
	};

	/**
	 * 獲取一般訂單選項
	 */
	const getOptionsApi = async () => {
		const orderOptionsRes = await getOrdersOptionsApi();

		// 處理一般訂單選項
		if (orderOptionsRes) {
			setOptions(orderOptionsRes);
		}
	};

	useEffect(
		() => {
			if (!preloadRef.current) return;
			preloadRef.current = false;
			const params = getParams(initBookmark(), activeTab);
			getOverviewApi(params);
		},
		// eslint-disable-next-line
		[activeTab]
	);

	useEffect(
		() => {
			if (!preloadRef.current) return;
			preloadRef.current = false;
			const params = getParams(bookmark, activeTab);
			getOverviewApi(params);
		},
		// eslint-disable-next-line
		[bookmark.pageIndex, activeTab]
	);

	// 重新整理||回上一頁後的功能
	useEffect(
		() => {
			if (isEmpty(options)) return;
			if (refreshQueryRef.current) return;
			// 檢查 URL 中是否有查詢參數，如果沒有參數則不執行搜尋（首次進入）
			const search = history.location.search;
			const hasQueryParams = search && search.length > 0 && search !== "?";
			if (!hasQueryParams) {
				refreshQueryRef.current = true;
				return;
			}
			refreshQueryRef.current = true;
			const query = getQueryStringParams();
			const params = getParams(bookmark, activeTab);
			const newParams = { ...params, ...query };
			setParamsData(newParams);
			getOverviewApi(newParams);
		},
		// eslint-disable-next-line
		[options, activeTab, bookmark.pageIndex]
	);

	useEffect(
		() => {
			getOptionsApi();
			setRefresh(handleSearchOnClick);
		},
		// eslint-disable-next-line
		[]
	);

	return (
		<Card className={clsx("search-bar-light p-3", className)}>
			<Card.CardContent>
				<Form onSubmit={handleSearchOnClick}>
					<Grid spacing={2} container>
						{/* 訂單/購買人 */}
						<Grid xs={4} item>
							<TextField key={name} ref={nameRef} label={"訂單/購買人"} defaultValue={name} InputProps={{ placeholder: "請輸入訂單編號或購買人或會員編號" }} fullWidth />
						</Grid>
						{/* 建立日期 */}
						<Grid xs={8} item>
							<DateIntervalField
								key={`${startDate}_${endDate}`}
								ref={dateRangeRef}
								label={"建立日期"}
								startDateProps={{
									defaultValue: parseDate(startDate),
								}}
								endDateProps={{
									defaultValue: parseDate(endDate),
								}}
								fullWidth
							/>
						</Grid>
						{/* 購買人身份 */}
						<Grid xs={4} item>
							<SelectField key={receiverType} label={"購買人身份"} ref={receiverTypeRef} defaultValue={receiverType} fullWidth>
								{Array.isArray(receiverTypeOptions) &&
									receiverTypeOptions
										.filter(item => item || !item.disabled)
										.map(item => (
											<MenuItem key={item.value} value={item.value}>
												{item.text}
											</MenuItem>
										))}
							</SelectField>
						</Grid>
						{/* 狀態 */}
						<Grid xs={4} item>
							<SelectField key={status} label={"狀態"} ref={statusRef} defaultValue={status} fullWidth>
								{Array.isArray(integrateStatusOptions) &&
									integrateStatusOptions
										.filter(item => item || !item.disabled)
										.map(item => (
											<MenuItem key={item.value} value={item.value}>
												{item.text}
											</MenuItem>
										))}
							</SelectField>
						</Grid>
						{/* 訂單類型 */}
						<Grid xs={4} item>
							<SelectField key={orderType} label={"訂單類型"} ref={orderTypeRef} defaultValue={orderType} fullWidth>
								{Array.isArray(orderTypeOptions) &&
									orderTypeOptions
										.filter(item => item || !item.disabled)
										.map(item => (
											<MenuItem key={item.value} value={item.value}>
												{item.text}
											</MenuItem>
										))}
							</SelectField>
						</Grid>
						{/* 搜尋按鈕 */}
						<Grid xs={12} textAlign={"right"} item>
							<Button className={"white-space-nowrap"} type={"submit"} variant={"contained"}>
								搜尋
							</Button>
						</Grid>
					</Grid>
				</Form>
			</Card.CardContent>
		</Card>
	);
};

export default AccountBookOrderOverviewSearchBar;
